// Quem está usando o app, em qual empresa e com qual papel.
//
// Sem sinal, o app abre com o que o aparelho já sabe: o último usuário que
// entrou e as contas dele ficam guardados no aparelho. Quando o sinal volta, o
// supabase-js renova a sessão sozinho e a sincronia envia o que ficou na fila.
import { isAuthRetryableFetchError } from '@supabase/supabase-js';
import { VERSAO_TERMOS } from './config.js';
import { diasEntre, hoje } from './lib/datas.js';
import * as armazem from './local/armazem.js';
import { abrirBanco, apagarBanco, fecharBanco, temBanco } from './local/banco.js';
import { ehErroDeRede, exclusivo, zerarEstado } from './local/sincronia.js';
import { comPrazo, executar, supabase } from './supabase.js';

const CHAVE_EMPRESA = 'maxcargo-empresa';
const CHAVE_USUARIO = 'maxcargo-usuario';
const CHAVE_SESSAO = 'maxcargo-sessao';

export const sessao = {
  usuario: null,
  vinculos: [], // [{ papel, empresa }]
  convites: [], // convites em aberto para o e-mail desta pessoa (só com internet)
  termosPendentes: false, // falta aceitar a versão atual dos Termos e da Política
  empresa: null,
  papel: null,
  colaborador: null, // motorista: o cadastro dele em Pessoas
  entrada: 0, // conta as entradas neste carregamento da página (os dados são baixados de novo a cada uma)
};

function lerLocal(chave) {
  try {
    return localStorage.getItem(chave);
  } catch {
    return null;
  }
}

function gravarLocal(chave, valor) {
  try {
    if (valor === null) localStorage.removeItem(chave);
    else localStorage.setItem(chave, valor);
  } catch {
    /* navegador sem armazenamento: segue sem lembrar */
  }
}

function usuarioGuardado() {
  try {
    const u = JSON.parse(lerLocal(CHAVE_USUARIO) ?? 'null');
    return u?.id ? u : null;
  } catch {
    return null;
  }
}

// Chegou pelo link do e-mail: a sessão nova está no endereço.
function entrandoPeloLink() {
  return /access_token=|refresh_token=/.test(location.hash) || /[?&]code=/.test(location.search);
}

export async function iniciarSessao() {
  // Com sinal fraco, renovar a sessão pode demorar: passados 6 s, abre com o
  // último usuário deste aparelho e a renovação continua por trás. Quem chega
  // pelo link do e-mail espera a entrada terminar (pode ser outra pessoa).
  const sessaoAuth = supabase.auth.getSession();
  let espera;
  try {
    espera = entrandoPeloLink() ? { valor: await sessaoAuth } : await comPrazo(sessaoAuth, 6000);
  } catch (erro) {
    espera = { falha: erro };
  }
  const { data, error } = espera.tempo
    ? { data: {}, error: { name: 'AuthRetryableFetchError', message: 'o servidor não respondeu em 6 s' } }
    : espera.falha ? { data: {}, error: espera.falha } : espera.valor;
  let usuario = data?.session?.user ?? null;
  if (!usuario && error && (espera.tempo || espera.falha || isAuthRetryableFetchError(error))) {
    // Sem internet (ou o servidor demorou) para renovar a sessão: segue com o
    // último usuário deste aparelho. Se ninguém entrou neste aparelho ainda,
    // segue sem usuário: a tela de entrada avisa quando precisar do servidor
    // (antes, o app parava aqui; aconteceu num iPhone em 08/10/2026).
    usuario = usuarioGuardado();
    if (!usuario) console.warn('Sessão não lida ao abrir o app:', error);
  }
  sessao.usuario = usuario;
  if (usuario) await prepararUsuario(usuario, { preferirGuardados: true });
}

// Depois de entrar (ou ao abrir o app): abre o banco do aparelho deste usuário
// e carrega as contas dele. Ao abrir o app, usa as contas guardadas no
// aparelho na hora e confere com o servidor por trás.
export async function prepararUsuario(usuario, { preferirGuardados = false } = {}) {
  sessao.usuario = usuario;
  sessao.entrada += 1;
  gravarLocal(CHAVE_USUARIO, JSON.stringify({ id: usuario.id, email: usuario.email ?? null, is_anonymous: Boolean(usuario.is_anonymous) }));
  await abrirBanco(usuario.id);
  if (preferirGuardados) {
    const guardados = await armazem.lerVinculos();
    if (guardados.length > 0) {
      usarVinculos(guardados);
      conferencia = carregarVinculos()
        .then(() => aoMudarConta?.())
        .catch(() => {});
      return;
    }
  }
  await carregarVinculos();
}

let aoMudarConta = null;
let conferencia = Promise.resolve();

// Termina quando as contas, os convites e o aceite dos termos desta pessoa
// forem conferidos com o servidor (ao abrir o app, isso corre por trás).
export function contaConferida() {
  return conferencia;
}

// Chamado depois de conferir as contas com o servidor por trás (a situação
// ou o papel podem ter mudado desde a última vez).
export function quandoAContaForConferida(fn) {
  aoMudarConta = fn;
}

function usarVinculos(vinculos) {
  sessao.vinculos = vinculos.map(({ papel, colaborador_id: colaborador = null, empresa }) => ({ papel, colaborador_id: colaborador, empresa }));
  const salvo = lerLocal(CHAVE_EMPRESA);
  const escolhido =
    sessao.vinculos.find((v) => v.empresa.id === salvo) ?? (sessao.vinculos.length === 1 ? sessao.vinculos[0] : null);
  escolherEmpresa(escolhido);
}

export async function carregarVinculos() {
  if (!sessao.usuario) return;
  const usuarioId = sessao.usuario.id;
  try {
    // Leitura e gravação juntas, sem cruzar com um envio: senão uma leitura
    // feita antes de a alteração da empresa chegar ao servidor poderia gravar
    // por cima a versão velha.
    const guardados = await exclusivo(async () => {
      const linhas = await executar(
        supabase
          .from('membros')
          .select('papel, colaborador_id, empresa_id, empresa:empresas(*)')
          .eq('user_id', usuarioId)
          .eq('ativo', true),
      );
      // Motorista e agenciador: a empresa vem resumida (sem o CPF/CNPJ e os
      // dados cadastrais).
      if (linhas.some((v) => (v.papel === 'motorista' || v.papel === 'agenciador') && !v.empresa)) {
        const resumos = (await executar(supabase.rpc('empresas_do_motorista'))) ?? [];
        for (const v of linhas) v.empresa ??= resumos.find((e) => e.id === v.empresa_id) ?? null;
      }
      if (sessao.usuario?.id !== usuarioId) return null; // saiu no meio
      // A sessão do servidor tem de ser desta pessoa (outra aba pode ter
      // entrado com outro e-mail): senão a lista viria errada e os dados das
      // empresas dela sairiam do aparelho.
      const { data: atual } = await supabase.auth.getSession();
      if (atual?.session?.user?.id !== usuarioId) return null;
      return armazem.guardarVinculos(linhas.filter((v) => v.empresa));
    });
    if (guardados) usarVinculos(guardados);
  } catch (erro) {
    if (!ehErroDeRede(erro)) throw erro;
    const guardados = await armazem.lerVinculos();
    if (guardados.length === 0) throw erro;
    usarVinculos(guardados);
    return;
  }
  await Promise.all([carregarConvites(usuarioId), carregarAceite(usuarioId)]);
}

// A pessoa já aceitou a versão atual dos Termos de uso e da Política de
// privacidade? Se não (conta de antes dos termos, ou texto novo), o Início
// pede o aceite. Sem internet ou sem a tabela no servidor, não pede.
async function carregarAceite(usuarioId) {
  try {
    const { data, error } = await supabase.from('aceites').select('versao').eq('versao', VERSAO_TERMOS);
    if (error) throw error;
    if (sessao.usuario?.id === usuarioId) sessao.termosPendentes = (data ?? []).length === 0;
  } catch {
    /* fica como está */
  }
}

// Convites em aberto para o e-mail desta pessoa. Sem internet (ou com o
// servidor sem a função ainda), segue sem eles: não atrapalham a entrada.
export async function carregarConvites(usuarioId = sessao.usuario?.id) {
  try {
    const { data, error } = await supabase.rpc('convites_para_mim');
    if (error) throw error;
    if (sessao.usuario?.id === usuarioId) sessao.convites = data ?? [];
  } catch (erro) {
    if (!ehErroDeRede(erro)) console.warn('Convites:', erro?.message ?? erro);
  }
}

export function escolherEmpresa(vinculo) {
  sessao.empresa = vinculo?.empresa ?? null;
  sessao.papel = vinculo?.papel ?? null;
  sessao.colaborador = vinculo?.colaborador_id ?? null;
  gravarLocal(CHAVE_EMPRESA, vinculo ? vinculo.empresa.id : null);
}

// Atualiza os dados da empresa atual (depois de editar a conta).
export function atualizarEmpresa(empresa) {
  sessao.empresa = empresa;
  const v = sessao.vinculos.find((x) => x.empresa.id === empresa.id);
  if (v) v.empresa = empresa;
}

export function hojeDaEmpresa() {
  return hoje(sessao.empresa?.fuso_horario);
}

export function ehTitular() {
  return sessao.papel === 'titular';
}

// App do motorista: entrou pelo link (sem e-mail) e só vê o que é dele.
export function ehMotorista() {
  return sessao.papel === 'motorista';
}

// App do agenciador (0.21.0): entrou pelo link e só vê as cargas dele.
export function ehAgenciador() {
  return sessao.papel === 'agenciador';
}

export function entrouSemEmail() {
  return Boolean(sessao.usuario?.is_anonymous);
}

// O motorista lança despesas (vínculo de motorista e conta gravável).
export function motoristaGrava() {
  return ehMotorista() && contaGravavel();
}

// Dias restantes do teste grátis (0 = último dia; negativo = vencido).
export function diasDeTeste() {
  if (!sessao.empresa || sessao.empresa.situacao !== 'teste') return null;
  return diasEntre(hojeDaEmpresa(), sessao.empresa.teste_ate);
}

// Mesma regra de app.conta_gravavel() no banco: a conta ativa grava até a
// carência (10 dias depois do vencimento da fatura mais antiga em aberto); a
// conta em teste, até o fim do teste e, se já assinou, até a carência.
export function contaGravavel() {
  const e = sessao.empresa;
  if (!e) return false;
  const hojeEmp = hojeDaEmpresa();
  const naCarencia = Boolean(e.carencia_ate) && e.carencia_ate >= hojeEmp;
  if (e.situacao === 'ativa') return !e.carencia_ate || naCarencia;
  return e.situacao === 'teste' && (e.teste_ate >= hojeEmp || naCarencia);
}

// Fatura vencida e ainda na carência: dia em que a conta fica só para consulta
// se não pagar (null quando não há fatura vencida).
export function prazoDaFatura() {
  const e = sessao.empresa;
  if (!e?.carencia_ate || !['ativa', 'teste'].includes(e.situacao)) return null;
  // A carência termina 10 dias depois do vencimento: venceu se faltam menos de 10.
  const faltam = diasEntre(hojeDaEmpresa(), e.carencia_ate);
  return faltam >= 0 && faltam < 10 ? e.carencia_ate : null;
}

export function podeGravar() {
  return contaGravavel() && (sessao.papel === 'titular' || sessao.papel === 'escritorio');
}

// Quantas alterações feitas neste aparelho ainda não chegaram ao servidor.
export async function alteracoesNaoEnviadas() {
  if (!temBanco()) return 0;
  return (await armazem.resumoDaFila()).total;
}

// A sessão acabou fora do app (renovação recusada, saiu em outra aba): limpa a
// memória e esquece o último usuário, para o app não abrir com ele sem sinal.
// O banco do aparelho fica: se a mesma pessoa entrar de novo, o que estava na
// fila ainda é enviado.
export function sessaoEncerrada() {
  sessao.usuario = null;
  sessao.vinculos = [];
  sessao.convites = [];
  sessao.termosPendentes = false;
  escolherEmpresa(null);
  gravarLocal(CHAVE_USUARIO, null);
  fecharBanco();
  zerarEstado();
}

export async function sair() {
  // Sai só deste aparelho; o escritório pode estar usando a mesma conta.
  // Sem sinal, o supabase-js não consegue avisar o servidor e deixaria a
  // sessão guardada: aí ela é apagada à mão.
  const { error } = await supabase.auth.signOut({ scope: 'local' }).catch((erro) => ({ error: erro }));
  if (error) {
    for (const chave of [CHAVE_SESSAO, `${CHAVE_SESSAO}-user`, `${CHAVE_SESSAO}-code-verifier`]) gravarLocal(chave, null);
  }
  const id = sessao.usuario?.id;
  sessao.usuario = null;
  sessao.vinculos = [];
  sessao.convites = [];
  sessao.termosPendentes = false;
  escolherEmpresa(null);
  gravarLocal(CHAVE_USUARIO, null);
  zerarEstado();
  // Os dados deste usuário saem do aparelho.
  if (id) await apagarBanco(id).catch(() => {});
}
